/**
 * instagram-feed.js — brand-neutral Instagram Feed section (see ADR-0004).
 *
 * Fetches the brand's Instagram posts client-side from the SkinLab CSA
 * Supabase project (ig_posts via PostgREST, publishable key; images come
 * from the public ig-media storage bucket — never Instagram's expiring CDN)
 * and renders them as square/portrait tiles in a 3-up grid, an auto-fill
 * grid capped at a fixed number of rows (two above 750px, three below — the
 * regular Instagram grid), or a 3-up scroll-snap slider. All filtering (caption/hashtag
 * keyword, date range, format, sort) is merchant-side (section settings).
 *
 * The slider ports the reviews-wall mechanics: arrows page by a full view,
 * lazy loading fetches visible cards × 2 per batch one viewport ahead, and
 * desktop mice can drag/flick the track (touch swipes scroll natively).
 *
 * Click behaviour: open the post's permalink in a new tab, or open an
 * embedded modal — a hand-built instagram-media blockquote upgraded by
 * Instagram's public embed.js (token-free; lazy-loaded on the first open).
 * If the embed fails to materialise, the modal falls back to the re-hosted
 * image + caption + a "View on Instagram" link. Never breaks the page.
 */
(() => {
  if (customElements.get('instagram-feed')) return;

  const SELECT_COLUMNS = [
    'media_id', 'permalink', 'shortcode', 'caption', 'format',
    'published_at', 'like_count', 'comments_count',
    'image_path', 'image_width', 'image_height',
  ].join(',');

  const SORTS = {
    newest: 'published_at.desc,media_id.desc',
    oldest: 'published_at.asc,media_id.asc',
    most_liked: 'like_count.desc.nullslast,published_at.desc,media_id.desc',
  };

  // Tile badges, matching Instagram's own grid glyphs: stacked squares for
  // carousels, a reel-style play glyph for reels/videos. Singles get none.
  const BADGE_CAROUSEL = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 3h11a2 2 0 0 1 2 2v11" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/><rect x="3" y="7" width="14" height="14" rx="2" fill="currentColor"/></svg>';
  const BADGE_REEL = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9.5 8.5l7 3.5-7 3.5z" fill="currentColor"/><rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="none" stroke="currentColor" stroke-width="2"/></svg>';

  const ICON_HEART = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 21C5.5 15.9 2 12.4 2 8.6 2 5.6 4.4 3.5 7.2 3.5c1.9 0 3.6 1 4.8 2.7 1.2-1.7 2.9-2.7 4.8-2.7C19.6 3.5 22 5.6 22 8.6c0 3.8-3.5 7.3-10 12.4z" fill="currentColor"/></svg>';
  const ICON_COMMENT = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 2.5c5.5 0 9.5 3.9 9.5 8.9s-4 8.9-9.5 8.9c-1.1 0-2.2-.2-3.2-.5l-4.6 1.7 1.3-4.2c-1.6-1.6-2.5-3.7-2.5-5.9 0-5 4-8.9 9.5-8.9z" fill="currentColor"/></svg>';

  const EMBED_SCRIPT_SRC = 'https://www.instagram.com/embed.js';
  const EMBED_UPGRADE_TIMEOUT_MS = 4000;

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* Cut a caption to `max` characters on a WORD boundary and add an ellipsis.
   * Cutting mid-word ("Our Signature Microneedl…") reads as a rendering bug
   * rather than as a deliberate excerpt. A caption already inside the budget is
   * returned untouched — no ellipsis on text that was never cut. */
  function truncate(text, max) {
    const limit = Number(max) > 0 ? Number(max) : 0;
    if (!text || !limit || text.length <= limit) return text || '';
    const cut = text.slice(0, limit);
    const space = cut.lastIndexOf(' ');
    /* A single word longer than the whole budget has no space to break on;
     * fall back to the hard cut rather than returning an empty string. */
    return (space > limit * 0.5 ? cut.slice(0, space) : cut).replace(/[\s.,;:!?-]+$/, '') + '…';
  }

  function formatCount(n) {
    if (n == null) return null;
    if (n >= 10000) return `${Math.round(n / 1000)}k`;
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return String(n);
  }

  class InstagramFeed extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-instagram-feed-grid]');
      this.status = this.querySelector('[data-instagram-feed-status]');
      this.modal = this.querySelector('[data-instagram-feed-modal]');
      this.modalBody = this.querySelector('[data-instagram-feed-modal-body]');
      const configEl = this.querySelector('[data-instagram-feed-config]');
      if (!this.grid || !configEl) return;

      let raw;
      try {
        raw = JSON.parse(configEl.textContent);
      } catch (err) {
        console.warn('[instagram-feed] invalid config JSON', err);
        return;
      }
      this.cfg = this.normalizeConfig(raw);
      this.isSlider = this.cfg.layout === 'slider';
      // The auto-fill grid shows a FIXED number of rows of however many
      // columns the CSS clamp generated, so its visible cell count is
      // viewport-dependent and the rows it holds are not all rendered.
      this.isCapped = this.cfg.layout === 'autofill';
      this.rows = [];
      this.loading = false;
      this.exhausted = false;
      this.abort = new AbortController();

      if (!this.cfg.supabaseUrl || !this.cfg.supabaseKey) {
        if (raw.designMode) this.showStatus('Instagram Feed: set the Supabase URL and publishable key in the section settings.');
        else console.warn('[instagram-feed] missing Supabase URL/key — section renders nothing');
        return;
      }

      if (this.isSlider) this.setupSlider();
      if (this.isCapped) this.watchColumns();
      if (this.cfg.clickBehavior === 'modal' && this.modal) this.setupModal();
      this.loadPage(true);
    }

    disconnectedCallback() {
      if (this.ro) this.ro.disconnect();
      if (this.abort) this.abort.abort();
      if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
    }

    normalizeConfig(raw) {
      const pick = (obj) => Object.keys(obj || {}).filter((k) => obj[k]);
      let formats = pick(raw.formats);
      // "Can't unselect everything" guard (reviews-wall convention): all
      // three format boxes unticked behaves as all ticked.
      if (formats.length === 0) formats = ['single', 'carousel', 'reel'];
      const date = (v) => (/^\d{4}-\d{2}-\d{2}$/.test((v || '').trim()) ? v.trim() : '');
      return {
        supabaseUrl: (raw.supabaseUrl || '').replace(/\/+$/, ''),
        supabaseKey: raw.supabaseKey || '',
        brand: (raw.brand || '').trim().toLowerCase(),
        keyword: (raw.keyword || '').trim(),
        dateFrom: date(raw.dateFrom),
        dateTo: date(raw.dateTo),
        formats,
        sort: SORTS[raw.sort] ? raw.sort : 'newest',
        layout: ['grid3', 'autofill', 'slider'].includes(raw.layout) ? raw.layout : 'grid3',
        maxPosts: Math.min(48, Math.max(3, parseInt(raw.maxPosts, 10) || 12)),
        clickBehavior: raw.clickBehavior === 'modal' ? 'modal' : 'newtab',
        labels: raw.labels || {},
      };
    }

    // Grids load everything at once (≤ 48 tiny rows). The slider shows 3 at
    // a time and lazy-loads visible × 2 per batch, reviews-wall style.
    pageSize() {
      const remaining = this.cfg.maxPosts - this.rows.length;
      return this.isSlider ? Math.min(6, remaining) : remaining;
    }

    buildParams(offset, limit) {
      const cfg = this.cfg;
      const params = new URLSearchParams();
      params.set('select', SELECT_COLUMNS);
      params.set('order', SORTS[cfg.sort]);
      params.set('limit', String(limit));
      if (offset) params.set('offset', String(offset));
      params.set('brand', `eq.${cfg.brand}`);
      if (cfg.formats.length < 3) {
        // The "Reels & videos" checkbox covers legacy non-reel feed video.
        const expanded = cfg.formats.flatMap((f) => (f === 'reel' ? ['reel', 'video'] : [f]));
        params.set('format', `in.(${expanded.join(',')})`);
      }
      if (cfg.keyword) params.set('caption', `wfts(simple).${cfg.keyword}`);
      // Merchant dates are Singapore time (workspace convention).
      if (cfg.dateFrom) params.append('published_at', `gte.${cfg.dateFrom}T00:00:00+08:00`);
      if (cfg.dateTo) params.append('published_at', `lte.${cfg.dateTo}T23:59:59+08:00`);
      return params;
    }

    async loadPage(isFirst) {
      if (this.loading || this.exhausted) return;
      this.loading = true;
      const cfg = this.cfg;
      const limit = this.pageSize();
      try {
        const res = await fetch(`${cfg.supabaseUrl}/rest/v1/ig_posts?${this.buildParams(this.rows.length, limit)}`, {
          headers: { apikey: cfg.supabaseKey, Authorization: `Bearer ${cfg.supabaseKey}` },
          signal: this.abort.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = await res.json();
        this.rows.push(...rows);
        if (this.isCapped) this.syncVisible();
        else this.renderRows(rows);
        if (isFirst && rows.length === 0) {
          this.showStatus(cfg.labels.empty || 'No posts to show yet.');
        } else {
          this.hideStatus();
        }
        this.exhausted = rows.length < limit || this.rows.length >= cfg.maxPosts;
        this.updateArrows();
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('[instagram-feed] fetch failed', err);
        // Fail soft: never break the page. Show the error text only when
        // nothing is rendered yet.
        if (!this.grid.children.length) this.showStatus(cfg.labels.error || 'Posts are unavailable right now.');
      } finally {
        this.loading = false;
      }
    }

    /* ------------------------------------------------------------------ *
     * Slider — a direct port of the reviews-wall mechanics.
     * ------------------------------------------------------------------ */

    setupSlider() {
      this.prevBtn = this.querySelector('[data-instagram-feed-prev]');
      this.nextBtn = this.querySelector('[data-instagram-feed-next]');
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollByView(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollByView(1));
      let ticking = false;
      this.grid.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            this.updateArrows();
            this.maybeLoadMore();
          });
        },
        { passive: true, signal: this.abort.signal }
      );
      window.addEventListener('resize', () => this.updateArrows(), {
        passive: true,
        signal: this.abort.signal,
      });
      this.setupDrag();
    }

    // Mouse drag-to-flick. Touch pointers are excluded — swiping already
    // scrolls the track natively. Mandatory snap re-snaps on every
    // programmatic scrollLeft write, so it is suspended (.is-dragging)
    // for the whole gesture and restored once the release glide settles.
    setupDrag() {
      const grid = this.grid;
      const opts = { signal: this.abort.signal };
      let dragging = false;
      let moved = false;
      let startX = 0;
      let startScroll = 0;
      let samples = [];

      grid.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType === 'touch' || e.button !== 0) return;
          dragging = true;
          moved = false;
          startX = e.clientX;
          startScroll = grid.scrollLeft;
          samples = [{ x: e.clientX, t: e.timeStamp }];
        },
        opts
      );

      grid.addEventListener(
        'pointermove',
        (e) => {
          if (!dragging) return;
          const dx = e.clientX - startX;
          if (!moved) {
            if (Math.abs(dx) < 6) return; // still a click, not a drag
            moved = true;
            try {
              grid.setPointerCapture(e.pointerId);
            } catch (err) {
              /* no active pointer (synthetic event) */
            }
            grid.classList.add('is-dragging');
            const sel = document.getSelection();
            if (sel) sel.removeAllRanges();
          }
          grid.scrollLeft = startScroll - dx;
          samples.push({ x: e.clientX, t: e.timeStamp });
          if (samples.length > 6) samples.shift();
        },
        opts
      );

      const release = (e) => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        // The click that follows a drag must not open a post.
        this.suppressClick = true;
        setTimeout(() => {
          this.suppressClick = false;
        }, 0);
        this.glide(e, samples);
      };
      grid.addEventListener('pointerup', release, opts);
      grid.addEventListener('pointercancel', release, opts);

      // Tiles are <img> inside <a> — suppress both native drag behaviours.
      grid.addEventListener('dragstart', (e) => e.preventDefault(), opts);
    }

    // Project the flick (recent pointer velocity × glide time), round the
    // destination to a card boundary, smooth-scroll there.
    glide(e, samples) {
      const grid = this.grid;
      const last = samples[samples.length - 1];
      const ref = samples.find((s) => last.t - s.t <= 120) || samples[0];
      const dt = last.t - ref.t;
      const velocity = dt > 0 ? (last.x - ref.x) / dt : 0; // px/ms; +ve = dragged right
      const step = this.cardStep();
      const projected = grid.scrollLeft - velocity * 280;
      const max = grid.scrollWidth - grid.clientWidth;
      const target = Math.max(0, Math.min(max, Math.round(projected / step) * step));
      grid.scrollTo({ left: target, behavior: 'smooth' });
      this.restoreSnapWhenSettled();
    }

    // Flipping mandatory snap back on mid-glide would yank the track, so
    // wait until the scroll position stops changing.
    restoreSnapWhenSettled() {
      if (this.settleTimer) clearInterval(this.settleTimer);
      let lastLeft = -1;
      this.settleTimer = setInterval(() => {
        if (!this.grid.isConnected) {
          clearInterval(this.settleTimer);
          return;
        }
        if (this.grid.scrollLeft === lastLeft) {
          clearInterval(this.settleTimer);
          this.settleTimer = null;
          this.grid.classList.remove('is-dragging');
          return;
        }
        lastLeft = this.grid.scrollLeft;
      }, 100);
    }

    cardStep() {
      const card = this.grid.querySelector('.instagram-feed__card');
      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : this.grid.clientWidth;
    }

    // Arrows page by a full view (three cards), the grid-page feel.
    scrollByView(dir) {
      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      this.grid.scrollBy({ left: dir * (this.grid.clientWidth + gap), behavior: 'smooth' });
    }

    // Lazy load: fetch the next batch one viewport before the end of the
    // loaded track, so cards are already there when the visitor arrives.
    maybeLoadMore() {
      if (!this.isSlider || this.exhausted || this.loading) return;
      if (this.grid.scrollLeft + this.grid.clientWidth * 2 >= this.grid.scrollWidth) {
        this.loadPage(false);
      }
    }

    updateArrows() {
      if (!this.prevBtn || !this.nextBtn) return;
      const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
      this.prevBtn.disabled = this.grid.scrollLeft <= 1;
      this.nextBtn.disabled = this.exhausted && this.grid.scrollLeft >= maxScroll - 1;
    }

    /* ------------------------------------------------------------------ *
     * Rendering
     * ------------------------------------------------------------------ */

    showStatus(text) {
      if (!this.status) return;
      this.status.textContent = text;
      this.status.hidden = false;
    }

    hideStatus() {
      if (this.status) this.status.hidden = true;
    }

    /* ------------------------------------------------------------------ *
     * Capped auto-fill grid — columns x rows visible cells.
     *
     * The column count is whatever the CSS clamp generated, so it is MEASURED
     * off the rendered grid and never recomputed here; the row target is read
     * out of the custom property instagram-feed.css sets, so the 750px
     * boundary is defined in exactly one place. Surplus posts are simply not
     * rendered — clipping a taller grid would still download their images.
     * ------------------------------------------------------------------ */

    measureTracks() {
      const cs = getComputedStyle(this.grid);
      const tracks = cs.gridTemplateColumns.trim().split(/\s+/);
      /* Fails OPEN. Before the grid has been laid out the computed value is
       * still the specified `repeat(auto-fill, ...)` string, and `none` comes
       * back for no tracks at all. Reading either as "1 column" would silently
       * ship a two-cell feed, so an unmeasurable grid reports 0 columns and
       * the caller renders everything it has — the pre-cap behaviour — and the
       * resize path corrects it on the next observed change. */
      const cols = tracks.length && tracks.every((t) => t.endsWith('px')) ? tracks.length : 0;
      const rows = parseInt(cs.getPropertyValue('--instagram-feed-rows'), 10);
      return { cols, rows: rows > 0 ? rows : 2 };
    }

    visibleTarget() {
      const { cols, rows } = this.measureTracks();
      if (!cols) return this.rows.length;
      // Capped by what the mirror actually returned: a short feed on a wide
      // screen is fewer cells, not a gap.
      return Math.min(cols * rows, this.rows.length);
    }

    // Idempotent: appends the missing tail / drops the surplus one. Never
    // rebuilds — a rebuild would re-request every image.
    syncVisible() {
      const target = this.visibleTarget();
      let count = this.grid.children.length;
      if (target > count) {
        const frag = document.createDocumentFragment();
        for (let i = count; i < target; i += 1) frag.appendChild(this.buildCard(this.rows[i]));
        this.grid.appendChild(frag);
      } else {
        while (count > target) {
          this.grid.lastElementChild.remove();
          count -= 1;
        }
      }
    }

    watchColumns() {
      let last = '';
      const sync = () => {
        const { cols, rows } = this.measureTracks();
        const key = `${cols}x${rows}`;
        /* Appending cards changes the grid's HEIGHT and ResizeObserver fires on
         * both axes, so without this cache the sync feeds itself. */
        if (key === last) return;
        last = key;
        this.syncVisible();
      };
      if ('ResizeObserver' in window) {
        // The grid, not the window: it also catches a container-width change.
        this.ro = new ResizeObserver(sync);
        this.ro.observe(this.grid);
      } else {
        window.addEventListener('resize', sync, { passive: true, signal: this.abort.signal });
      }
    }

    renderRows(rows) {
      const frag = document.createDocumentFragment();
      for (const row of rows) frag.appendChild(this.buildCard(row));
      this.grid.appendChild(frag);
      requestAnimationFrame(() => this.updateArrows());
    }

    buildCard(row) {
      const card = el('a', 'instagram-feed__card');
      card.href = row.permalink;
      card.target = '_blank';
      card.rel = 'noopener';
      const caption = (row.caption || '').trim().replace(/\s+/g, ' ');
      card.setAttribute('aria-label', caption ? `Instagram post: ${caption.slice(0, 80)}` : 'Instagram post');

      const img = document.createElement('img');
      img.className = 'instagram-feed__image';
      img.src = `${this.cfg.supabaseUrl}/storage/v1/object/public/ig-media/${row.image_path}`;
      img.alt = '';
      img.loading = 'lazy';
      if (row.image_width) img.width = row.image_width;
      if (row.image_height) img.height = row.image_height;
      card.appendChild(img);

      if (row.format === 'carousel' || row.format === 'reel' || row.format === 'video') {
        const badge = el('span', 'instagram-feed__badge');
        badge.innerHTML = row.format === 'carousel' ? BADGE_CAROUSEL : BADGE_REEL;
        card.appendChild(badge);
      }

      // Instagram-style hover overlay: the caption on top, the like/comment
      // counts (as of the last mirror sync) below. Decorative — the caption is
      // already in the card's aria-label, so the whole overlay stays out of the
      // accessibility tree rather than reading everything twice.
      const likes = formatCount(row.like_count);
      const comments = formatCount(row.comments_count);
      const blurb = truncate(
        caption,
        /* Fallback, not just a default: a NEW setting pinned in a template
         * JSON by the same merge that adds it to the schema is silently
         * dropped by Shopify. Without this, a dropped key would print the
         * ENTIRE caption over the tile. */
        this.cfg.captionChars > 0 ? this.cfg.captionChars : 140
      );
      if (blurb || likes != null || comments != null) {
        const overlay = el('span', 'instagram-feed__overlay');
        overlay.setAttribute('aria-hidden', 'true');

        // Always appended, even when empty: the overlay is a column with
        // `space-between`, which needs two children to push the stats to the
        // bottom. A captionless post would otherwise centre its stats.
        const cap = el('span', 'instagram-feed__caption');
        cap.textContent = blurb;
        overlay.appendChild(cap);

        const stats = el('span', 'instagram-feed__stats');
        if (likes != null) {
          const stat = el('span', 'instagram-feed__stat');
          stat.innerHTML = `${ICON_HEART}<span>${likes}</span>`;
          stats.appendChild(stat);
        }
        if (comments != null) {
          const stat = el('span', 'instagram-feed__stat');
          stat.innerHTML = `${ICON_COMMENT}<span>${comments}</span>`;
          stats.appendChild(stat);
        }
        overlay.appendChild(stats);

        card.appendChild(overlay);
      }

      card.addEventListener('click', (e) => {
        if (this.suppressClick) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        if (this.cfg.clickBehavior === 'modal' && this.modal) {
          e.preventDefault();
          this.openModal(row);
        }
      });

      return card;
    }

    /* ------------------------------------------------------------------ *
     * Modal — hand-built instagram-media blockquote + embed.js (ADR-0004).
     * ------------------------------------------------------------------ */

    setupModal() {
      const close = this.querySelector('[data-instagram-feed-modal-close]');
      if (close) close.addEventListener('click', () => this.modal.close());
      // Clicks on the backdrop land on the <dialog> itself; the inner
      // wrapper swallows everything else.
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) this.modal.close();
      });
      this.modal.addEventListener('close', () => {
        if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
        this.modalBody.innerHTML = '';
        document.body.style.overflow = '';
      });
    }

    // Instagram's embed script is loaded once, on the first modal open —
    // visitors who never open a post never load a Meta script.
    loadEmbedScript() {
      if (this.constructor.embedPromise) return this.constructor.embedPromise;
      this.constructor.embedPromise = new Promise((resolve, reject) => {
        if (window.instgrm && window.instgrm.Embeds) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = EMBED_SCRIPT_SRC;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('embed.js failed to load'));
        document.head.appendChild(script);
      });
      return this.constructor.embedPromise;
    }

    openModal(row) {
      if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
      this.modalBody.innerHTML = '';
      const quote = document.createElement('blockquote');
      quote.className = 'instagram-media';
      quote.setAttribute('data-instgrm-permalink', row.permalink);
      quote.setAttribute('data-instgrm-version', '14');
      quote.setAttribute('data-instgrm-captioned', '');
      this.modalBody.appendChild(quote);
      this.modal.showModal();
      document.body.style.overflow = 'hidden';

      const fallback = () => this.renderModalFallback(row);
      this.loadEmbedScript()
        .then(() => {
          if (!this.modal.open) return;
          window.instgrm.Embeds.process();
          // Fail-soft: if the blockquote never upgrades to an iframe
          // (script blocked mid-flight, permalink rejected), swap in the
          // re-hosted image + caption + link out.
          this.fallbackTimer = setTimeout(() => {
            if (this.modal.open && !this.modalBody.querySelector('iframe')) fallback();
          }, EMBED_UPGRADE_TIMEOUT_MS);
        })
        .catch(() => {
          if (this.modal.open) fallback();
        });
    }

    renderModalFallback(row) {
      this.modalBody.innerHTML = '';
      const wrap = el('div', 'instagram-feed__modal-fallback');
      const img = document.createElement('img');
      img.src = `${this.cfg.supabaseUrl}/storage/v1/object/public/ig-media/${row.image_path}`;
      img.alt = '';
      if (row.image_width) img.width = row.image_width;
      if (row.image_height) img.height = row.image_height;
      wrap.appendChild(img);
      if (row.caption) wrap.appendChild(el('p', 'instagram-feed__modal-caption', row.caption));
      const link = el('a', 'instagram-feed__modal-link', this.cfg.labels.viewOnInstagram || 'View on Instagram');
      link.href = row.permalink;
      link.target = '_blank';
      link.rel = 'noopener';
      wrap.appendChild(link);
      this.modalBody.appendChild(wrap);
    }
  }

  customElements.define('instagram-feed', InstagramFeed);
})();
